/**
 * The rating card under a reply preview (fr-docs `docs/smith-ams` T-117 / F-030).
 *
 * The preview iframe is `sandbox=""` on purpose — no scripts, no navigation — which leaves
 * the stars inside an answer inert. This card offers the same rating natively: the token is
 * read out of the reply (`ratingTokenIn`), the context comes from the same public relay the
 * e-mail link uses, and the form is the public page's `RatingPanel`. It renders only when
 * the reply carries a rating link minted for this run, and says so plainly when that link
 * has expired.
 *
 * Only a run launched from the portal has a reply here at all: the reply comes from the
 * result this BFF stored while it streamed the run, and a run that arrived by e-mail
 * (mail-reader) has none, so its answer is rated from the e-mail alone.
 */
import { Notice, Spinner } from '@evt/flow-stream-ui'
import { isInvalidFeedbackLink } from '../api/feedback'
import { useFeedbackContext } from '../hooks/use-feedback-context'
import { describeApiError } from '../i18n/errors'
import { useI18n } from '../i18n/I18nProvider'
import { RatingPanel } from './RatingPanel'
import { readRatingToken } from './rating-link'

export function RunRating({ token }: { token: string }) {
  const { t, formatDate } = useI18n()
  const context = useFeedbackContext(token)
  const expiresAt = readRatingToken(token)?.expiresAt

  return (
    <section className="result-rating" aria-labelledby="result-rating-title">
      <div className="result-section-label" id="result-rating-title">
        {t.feedback.pageTitle}
      </div>
      {context.isError && isInvalidFeedbackLink(context.error) ? (
        <p className="muted result-rating-note">
          {expiresAt && expiresAt.getTime() <= Date.now()
            ? t.feedback.inlineClosedOn(formatDate(expiresAt))
            : t.feedback.inlineClosed}
        </p>
      ) : context.isPending ? (
        <div className="rate-loading" aria-live="polite">
          <Spinner size={16} /> {t.common.loading}
        </div>
      ) : context.isError ? (
        <div className="login-body">
          <Notice kind="danger">{describeApiError(t, context.error)}</Notice>
          <button type="button" className="btn btn-sm" onClick={() => void context.refetch()}>
            {t.common.retry}
          </button>
        </div>
      ) : (
        <>
          <p className="muted result-rating-note">{t.feedback.subtitle}</p>
          <RatingPanel token={token} context={context.data} />
        </>
      )}
    </section>
  )
}
